import { AlertCircle } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'

interface ErrorFallbackProps {
  error?: Error
  /**
   * What the "Try again" button does. Omit it and no button is drawn — which makes an error
   * screen a dead end, so only omit it when there is genuinely nothing to retry or go back to.
   *
   * Named `onRetry` because that is the name every call site reached for. It used to be
   * `resetError`, and nine of the eleven callers passed `onRetry` or `resetErrorBoundary`
   * instead. React drops an unknown prop silently, so all nine rendered an error with no way out
   * and nothing reported it — the admin `tsc` script checks zero files, so the type errors sat
   * unseen. Renaming to the name people already used fixed six of them without touching their
   * code. Do not add an alias: two names for one prop is how this started.
   */
  onRetry?: () => void
}

export function ErrorFallback({ error, onRetry }: ErrorFallbackProps) {
  return (
    <div className="flex h-full items-center justify-center p-8">
      <Alert variant="destructive" className="max-w-2xl">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Something went wrong</AlertTitle>
        <AlertDescription className="space-y-4">
          <p>{error?.message || 'An unexpected error occurred'}</p>
          {onRetry && (
            <Button onClick={onRetry} variant="outline" size="sm">
              Try again
            </Button>
          )}
        </AlertDescription>
      </Alert>
    </div>
  )
}
